import { ChevronDownIcon, InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsArrowRight } from 'react-icons/bs';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import CustomCheckboxIcon from 'components/Icons/CustomCheckboxIcon';
import {
  getDataSourceApiPreviewEnabled,
  getNoheadersStatus,
  getSelectedDataFileDetails,
  getSelectedVersionDetails,
  getTableHeader,
  handleActionStepNext,
  resetSingleTimePeriodValue,
  setClearSelectedAreasTable,
  setMultiTimePeriodValue,
  setSelectedAreasProperties,
  setTimePeriodRowIndex,
} from 'redux/DataImportSlice';
import { getModelTime } from 'redux/IndicatorPageSlice';
import { textStyles } from 'pages/ModelOverviewPage/Import/Helper';

import ActionStepFooter from '../../ActionStepFooter';

import { loadTimeRowOptions, type TimeColumnMapping, type TimeRowOption } from './timeRowOptions';

interface Properties {
  setHelpModalSeleceted?: any;
  getFormValue?: any;
}

const validationSchema = Yup.object().shape({
  selectedRowIndex: Yup.string().required('A time period row is required'),
});

type TimePeriodFormValues = {
  selectedRowIndex: string;
  periodMappings: TimeColumnMapping[];
};

const applySavedMappings = (
  mappings: TimeColumnMapping[],
  savedMappings: Array<{ key: string; value: string | null }> | null | undefined,
  modelPeriods: string[],
) => {
  if (!Array.isArray(savedMappings)) return mappings;

  const validModelPeriods = new Set(modelPeriods.map(String));
  const savedByColumn = new Map(
    savedMappings.map((mapping) => [mapping.key, mapping.value ?? null]),
  );
  return mappings.map((mapping) => {
    if (!savedByColumn.has(mapping.key)) return mapping;

    const savedValue = savedByColumn.get(mapping.key);
    if (!savedValue || !validModelPeriods.has(String(savedValue))) return mapping;

    return {
      ...mapping,
      value: savedValue,
      autoMapped: true,
    };
  });
};

const getMappedPeriodCount = (mappings: TimeColumnMapping[]) =>
  mappings.filter((mapping) => Boolean(mapping.value)).length;

/** Keeps CSV preview ticks/highlights in sync with mapped time columns. */
const TimePeriodColumnHighlighter = ({
  loadingRows,
  periodMappings,
}: {
  loadingRows: boolean;
  periodMappings: TimeColumnMapping[];
}) => {
  const dispatch: any = useDispatch();
  const mappedHeadersKey = periodMappings
    .filter((entry) => entry.key && entry.value)
    .map((entry) => entry.key)
    .join('\0');

  useEffect(() => {
    if (loadingRows) return;

    const mappedHeaders = mappedHeadersKey ? mappedHeadersKey.split('\0') : [];
    const payload: Record<string, string> = { selectedColumnOption: '' };
    mappedHeaders.forEach((header, index) => {
      payload[`timePeriodMap.${index}`] = header;
    });

    dispatch(setClearSelectedAreasTable());
    dispatch(setSelectedAreasProperties(payload));
  }, [dispatch, loadingRows, mappedHeadersKey]);

  return null;
};

const Columns = ({ setHelpModalSeleceted, getFormValue }: Properties) => {
  const dispatch: any = useDispatch();
  const headerNames = useSelector(getTableHeader);
  const selectedVersion = useSelector(getSelectedVersionDetails);
  const selectedFile = useSelector(getSelectedDataFileDetails);
  const useApiPreview = useSelector(getDataSourceApiPreviewEnabled);
  const modelTime = useSelector(getModelTime);
  const noHeaders = useSelector(getNoheadersStatus);
  const [rowOptions, setRowOptions] = useState<TimeRowOption[]>([]);
  // Start true so we don't flash an empty/error state before the first load runs.
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [manualMapping, setManualMapping] = useState(false);
  const modelPeriods = useMemo(
    () => (Array.isArray(modelTime?.time_range) ? modelTime.time_range : []),
    [modelTime?.time_range],
  );
  const VStackContainer: any = VStack;
  const MenuTrigger: any = MenuButton;
  const BodyText: any = Text;
  const FormControlContainer: any = FormControl;
  const FlexContainer: any = Flex;
  const MenuContainer: any = Menu;
  const MenuOptions: any = MenuList;
  const MenuGroup: any = MenuOptionGroup;
  const MenuOption: any = MenuItemOption;
  const ManualMapCheckbox: any = Checkbox;

  useEffect(() => {
    let isCancelled = false;

    const loadRowCandidates = async () => {
      if (!selectedVersion?.s3_file_url || !headerNames?.length || modelPeriods.length === 0) {
        setRowOptions([]);
        // Stay in loading while prerequisites are still resolving (e.g. model time).
        setLoadingRows(true);
        setLoadError('');
        return;
      }

      setLoadingRows(true);
      setLoadError('');

      try {
        const options = await loadTimeRowOptions({
          s3FileUrl: selectedVersion.s3_file_url,
          headerNames,
          modelTimeRange: modelPeriods,
          dataSourceId: selectedFile?.id,
          versionId: selectedVersion?.id,
          useApiPreview,
        });
        if (isCancelled) return;
        setRowOptions(options);
        setLoadingRows(false);
      } catch {
        if (isCancelled) return;
        setRowOptions([]);
        setLoadError('Unable to read the selected file for time period detection.');
        setLoadingRows(false);
      }
    };

    loadRowCandidates();

    return () => {
      isCancelled = true;
    };
  }, [
    headerNames,
    modelPeriods,
    noHeaders,
    selectedVersion?.s3_file_url,
    selectedVersion?.id,
    selectedFile?.id,
    useApiPreview,
  ]);

  const initialFormValues = useMemo<TimePeriodFormValues>(() => {
    const saved = getFormValue?.timePeriodRowIndex;
    let selectedRowIndex = '';
    if (saved != null && rowOptions.some((row) => row.index === Number(saved))) {
      selectedRowIndex = String(saved);
    } else if (rowOptions.length > 0) {
      const bestRow = rowOptions.reduce((best, current) => {
        if (current.matchedCount !== best.matchedCount) {
          return current.matchedCount > best.matchedCount ? current : best;
        }
        return current.detectedCount > best.detectedCount ? current : best;
      });
      selectedRowIndex = String(bestRow.index);
    }

    const selectedRow = rowOptions.find((row) => String(row.index) === String(selectedRowIndex));
    return {
      selectedRowIndex,
      periodMappings: applySavedMappings(
        selectedRow?.mappings || [],
        getFormValue?.multiTimePeriodValue,
        modelPeriods,
      ),
    };
  }, [
    getFormValue?.multiTimePeriodValue,
    getFormValue?.timePeriodRowIndex,
    modelPeriods,
    rowOptions,
  ]);

  const staleSavedMappingCount = useMemo(() => {
    if (!Array.isArray(getFormValue?.multiTimePeriodValue)) return 0;

    const validModelPeriods = new Set(modelPeriods.map(String));
    return getFormValue.multiTimePeriodValue.filter(
      (mapping: { value?: string | null }) =>
        mapping?.value && !validModelPeriods.has(String(mapping.value)),
    ).length;
  }, [getFormValue?.multiTimePeriodValue, modelPeriods]);

  const getRowLabel = (value: string) =>
    rowOptions?.find((row) => String(row.index) === String(value))?.label;

  const handleSubmit = async (
    form: TimePeriodFormValues,
    setSubmitting: (data: boolean) => void,
  ) => {
    const selectedRow = rowOptions.find(
      (row) => String(row.index) === String(form.selectedRowIndex),
    );
    const mappingsForPayload = (form.periodMappings || [])
      .filter((mapping) => mapping?.key && mapping?.value)
      .map((mapping) => ({
        key: mapping.key,
        value: mapping.value,
      }));

    if (!selectedRow || mappingsForPayload.length === 0) {
      setSubmitting(false);
      return;
    }

    await dispatch(setTimePeriodRowIndex(selectedRow.index));
    await dispatch(setMultiTimePeriodValue(mappingsForPayload));
    await dispatch(resetSingleTimePeriodValue());
    await setSubmitting(false);
    await dispatch(handleActionStepNext());
  };

  return (
    <VStackContainer w='100%' pt={3}>
      <BodyText
        color='black'
        fontSize={textStyles.fontSize}
        fontFamily={textStyles.fontFamily}
        fontWeight={textStyles.fontWeight}
        textAlign='start'
        width='100%'
      >
        Select the row which has the time periods for each column. We&apos;ll auto-map any matching
        period labels to the model timeline.
      </BodyText>
      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue, isSubmitting }: any) => {
          const selectedRowLabel = getRowLabel(values?.selectedRowIndex);
          const rowPlaceholder = loadingRows ? 'Detecting rows…' : 'Select row';
          const periodMappings: TimeColumnMapping[] = values?.periodMappings || [];
          const mappedPeriodCount = getMappedPeriodCount(periodMappings);
          const hasUnmappedPeriods = periodMappings.some((mapping) => !mapping.value);
          const mappingControlsEnabled = manualMapping || hasUnmappedPeriods;
          const updatePeriodMapping = async (mappingIndex: number, period: string) => {
            const nextMappings = periodMappings.map((mapping, index) =>
              index === mappingIndex
                ? {
                    ...mapping,
                    value: period || null,
                    autoMapped: false,
                  }
                : mapping,
            );
            await setFieldValue('periodMappings', nextMappings);
          };

          return (
            <Form
              style={{
                width: '100%',
              }}
            >
              <TimePeriodColumnHighlighter
                loadingRows={loadingRows}
                periodMappings={periodMappings}
              />
              <FormControlContainer pt={2}>
                <MenuContainer closeOnSelect={true} isLazy>
                  <MenuTrigger
                    as={Button}
                    backgroundColor='white'
                    maxHeight='40px'
                    borderRadius='5px'
                    minWidth='140px'
                    textAlign='left'
                    padding='0 .8rem'
                    boxShadow='rgb(139, 137, 177) 0px 0px 0px 0.6px'
                    rightIcon={<ChevronDownIcon fontSize='md' />}
                    fontWeight='300'
                    fontSize='xs'
                    color='black.900'
                    w='100%'
                    border={errors.selectedRowIndex ? '2px solid #fe6e6e' : '0.6px solid #8B89B1'}
                    isDisabled={loadingRows || rowOptions.length === 0}
                  >
                    <BodyText
                      me={3}
                      noOfLines={1}
                      height='fit-content'
                      opacity={selectedRowLabel ? 1 : 0.5}
                    >
                      {selectedRowLabel ?? rowPlaceholder}
                    </BodyText>
                  </MenuTrigger>
                  <MenuOptions
                    height='fit-content'
                    maxHeight='20vh'
                    overflowY='auto'
                    className='scroll'
                  >
                    <MenuGroup
                      type='radio'
                      value={values?.selectedRowIndex}
                      onChange={async (role: any) => {
                        await setFieldValue('selectedRowIndex', role);
                        const selectedRow = rowOptions.find(
                          (row) => String(row.index) === String(role),
                        );
                        await setFieldValue('periodMappings', selectedRow?.mappings || []);
                        setManualMapping(false);
                      }}
                    >
                      {rowOptions.map((row) => (
                        <MenuOption key={row.index} value={String(row.index)}>
                          {row.label} ({row.matchedCount}/{row.detectedCount} matched)
                        </MenuOption>
                      ))}
                    </MenuGroup>
                  </MenuOptions>
                </MenuContainer>
                <ErrorMessage name='selectedRowIndex' component='div' className='error' />
              </FormControlContainer>

              {periodMappings.length > 0 && (
                <VStackContainer align='stretch' spacing={3} pt={4} w='100%'>
                  <FlexContainer gap={2} w='100%' py={2}>
                    <ManualMapCheckbox
                      background='transparent'
                      border='2px solid black'
                      borderColor='black'
                      className='checkboxStyle'
                      icon={<CustomCheckboxIcon isChecked={mappingControlsEnabled} />}
                      isChecked={mappingControlsEnabled}
                      isDisabled={hasUnmappedPeriods}
                      onChange={(event: any) => setManualMapping(event.target.checked)}
                    ></ManualMapCheckbox>
                    <BodyText color='black' fontSize='0.9rem' fontFamily='Roboto Regular'>
                      Map manually ?
                    </BodyText>
                  </FlexContainer>

                  {hasUnmappedPeriods && (
                    <BodyText color='#B45309' fontSize='xs'>
                      Some detected headers do not match the model timeline. Select target periods
                      below; unmapped columns will be skipped.
                    </BodyText>
                  )}

                  {staleSavedMappingCount > 0 && (
                    <BodyText color='#B45309' fontSize='xs'>
                      Some saved time period mappings are no longer in the current model timeline.
                      Please remap them before continuing.
                    </BodyText>
                  )}

                  <BodyText
                    color='#6562CF'
                    fontSize='lg'
                    fontFamily='regular_roboto'
                    fontWeight='400'
                    textAlign='start'
                    py={1}
                  >
                    Map the values into Blox
                  </BodyText>

                  <FlexContainer justifyContent='space-between' alignItems='center' w='100%'>
                    <BodyText
                      fontSize='1rem'
                      color='black'
                      fontFamily='regular_roboto'
                      fontWeight='600'
                    >
                      Time period in File
                    </BodyText>
                    <FlexContainer w='41%'>
                      <BodyText
                        fontSize='1rem'
                        color='black'
                        fontFamily='regular_roboto'
                        fontWeight='600'
                      >
                        Time period in Blox
                      </BodyText>
                    </FlexContainer>
                  </FlexContainer>

                  {periodMappings.map((mapping, index) => (
                    <FlexContainer
                      key={`${mapping.key}-${index}`}
                      alignItems='center'
                      justifyContent='space-between'
                      pt={3}
                      w='100%'
                    >
                      <FlexContainer alignItems='center' justifyContent='start' w='45%'>
                        <BodyText color='black' fontSize='xs' noOfLines={1}>
                          {mapping.sourceValue || mapping.key}
                        </BodyText>
                      </FlexContainer>
                      <FlexContainer alignItems='center' gap={2} justifyContent='end' w='50%'>
                        <BsArrowRight color='black' style={{ width: '20px', height: '20px' }} />
                        <MenuContainer closeOnSelect={true} isLazy>
                          <MenuTrigger
                            as={Button}
                            backgroundColor='white'
                            border='0.6px solid #8B89B1'
                            borderRadius='5px'
                            boxShadow='rgb(139, 137, 177) 0px 0px 0px 0.6px'
                            color='black.900'
                            fontSize='xs'
                            fontWeight='300'
                            isDisabled={!mappingControlsEnabled}
                            maxHeight='40px'
                            minWidth='140px'
                            padding='0 .8rem'
                            rightIcon={<ChevronDownIcon fontSize='md' />}
                            textAlign='left'
                            w='100%'
                          >
                            <BodyText me={3} noOfLines={1} opacity={mapping.value ? 1 : 0.5}>
                              {mapping.value || 'Select period'}
                            </BodyText>
                          </MenuTrigger>
                          <MenuOptions
                            className='scroll'
                            height='fit-content'
                            maxHeight='20vh'
                            overflowY='auto'
                          >
                            <MenuGroup
                              type='radio'
                              value={mapping.value || ''}
                              onChange={(period: any) => updatePeriodMapping(index, period)}
                            >
                              <MenuOption value=''>Skip this column</MenuOption>
                              {modelPeriods.map((period: string) => (
                                <MenuOption key={period} value={period}>
                                  {period}
                                </MenuOption>
                              ))}
                            </MenuGroup>
                          </MenuOptions>
                        </MenuContainer>
                      </FlexContainer>
                    </FlexContainer>
                  ))}
                </VStackContainer>
              )}

              {!loadingRows && rowOptions.length === 0 && (
                <BodyText color='red' fontSize='xs' pt={2}>
                  No date-like time period rows were detected in the selected file.
                </BodyText>
              )}

              {loadError && (
                <BodyText color='red' fontSize='xs' pt={2}>
                  {loadError}
                </BodyText>
              )}

              <FlexContainer
                alignItems='center'
                justifyContent='space-between'
                pb={8}
                pt='5'
                w='100%'
              >
                <FlexContainer>
                  <BodyText
                    {...textStyles}
                    color='black'
                    textDecoration='underline'
                    onClick={() => {
                      setHelpModalSeleceted.on();
                    }}
                    display='none'
                  >
                    Help <InfoIcon color='#8B89B1' ms={3} />
                  </BodyText>
                </FlexContainer>
                <ActionStepFooter
                  nextButtonName='Continue'
                  prevButtonName='Previous'
                  isSubmitting={isSubmitting}
                  disableNext={loadingRows || rowOptions.length === 0 || mappedPeriodCount === 0}
                />
              </FlexContainer>
            </Form>
          );
        }}
      </Formik>
    </VStackContainer>
  );
};

export default Columns;
